import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'

import DataTable from './components/DataTable'
import LegendTable from '../../../../../../../../components/LegendTable'
import CustomDate from '../../../../../../../../components/CustomDate'
import Counter from '../../../../../../../../components/Counter'
import GdprIcon from '../../../../../../../../components/GdprIcon'

import PaymentInformationsTerminology from './components/PaymentInformationsTerminology'
import PaymentInformationChart from './components/Chart'
import PaymentBreakdown from './components/PaymentBreakdown'

import { LOCALE_PREFIX } from '../Blocks/PaymentIndex/constants'
import { types } from './constants'

import hocConnect from './hocConnect'
import './style.scss'

@hocConnect
export default class PaymentInformations extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        breakdown: PropTypes.object,
        customData: PropTypes.object,
        data: PropTypes.object,
        graphData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        id: PropTypes.string.isRequired,
        legend: PropTypes.object,
        module: PropTypes.string.isRequired,
        level: PropTypes.string.isRequired,
        selector: PropTypes.string,
        originalData: PropTypes.array,
        translates: PropTypes.shape({
            firm: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sector: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            numberOfSuppliers: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            numberOfInvoices: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            paymentDue: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            paymentDueAverage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            paymentDueModus: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            paymentDueMax: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            riskIndicatorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            twelveMonthsTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            threeMonthsTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            dueDaysFor12monthsText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            dueDaysFor3monthsText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            interval: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            day: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            days: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            daysTwo: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyToggleText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            riskIndicatorInfoTextYear: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            riskIndicatorInfoTextQuarter: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        }).isRequired,
    }

    getRiskIndicator = () => this.props?.data?.subject?.year?.riskIndicator

    getIcon = (isActiveSubject, isActiveSector) => {
        if (isActiveSubject && isActiveSector) return 'combined'
        if (isActiveSubject) return 'subject'
        if (isActiveSector) return 'sector'

        return null
    }

    getValue = (type, isActiveSubject, isActiveSector) => {
        const paymentIndex = this.props?.data?.subject?.[type]?.paymentIndex
        const sectorPaymentIndex = this.props?.data?.sector?.[type]?.paymentIndex

        if (isActiveSubject && isActiveSector) {
            return (
                <span>
                    <span>{`${paymentIndex}`}</span>
                    <span
                        style={{
                            color: '#111111',
                            fontFamily: 'Trivia Sans Regular, sans-serif',
                            fontWeight: 'normal',
                        }}
                    >
                        {'/'}
                    </span>
                    <span
                        style={{ color: 'purple', fontFamily: 'Trivia Sans Regular, sans-serif', fontWeight: 'normal' }}
                    >
                        {`${sectorPaymentIndex}`}
                    </span>
                </span>
            )
        }

        if (isActiveSubject) return paymentIndex

        if (isActiveSector) return sectorPaymentIndex

        return null
    }

    getValueColor = (inactiveSubjectPI, legendItem) => {
        if (inactiveSubjectPI) return '#b3b3b3'

        return legendItem.color
    }

    getInterval = (legendItem) => {
        if (!legendItem.lowerBound) return 'N/A'

        return `${legendItem.lowerBound} - ${legendItem.upperBound}`
    }

    getSetup = (type) =>
        _.map(this.props?.legend?.[type], (legendItem) => {
            const isActiveSubject = this.props?.data?.subject?.[type]?.code === legendItem.code
            const isActiveSector = this.props?.data?.sector?.[type]?.code === legendItem.code
            const subjectPaymentIndexInactive = this.props?.data?.subject?.[type]?.paymentIndexInactive

            return {
                ...legendItem,
                active: isActiveSubject || isActiveSector,
                setup: [
                    {
                        identifier: 'icon',
                        type: this.getIcon(isActiveSubject, isActiveSector),
                        style: { width: '39px' },
                        header: { width: '39px' },
                    },
                    { identifier: 'thermometer', style: { width: '0px' } },
                    {
                        identifier: 'label',
                        style: {
                            minWidth: '151px',
                            color: this.getValueColor(subjectPaymentIndexInactive, legendItem),
                            borderRight: '1px solid #999',
                            fontWeight: isActiveSubject ? 900 : 'auto',
                            fontSize: isActiveSubject ? '30px' : '18px',
                            lineHeight: isActiveSubject ? '30px' : 'auto',
                        },
                        arrow: true,
                        colors: {
                            border: isActiveSubject ? '#8A9D1E' : '#662D91',
                            background: isActiveSubject ? '#F5F7E9' : '#F0EAF4',
                        },
                        value: this.getValue(type, isActiveSubject, isActiveSector),
                        header: {
                            label:
                                type === 'year' ? (
                                    <div>
                                        {this.props.translates.dueDaysFor12monthsText}
                                        <CustomDate
                                            date={_.get(this.props.data, 'subject.year.endDate.value')}
                                            precision={_.get(this.props.data, 'subject.year.endDate.precision')}
                                            precisionFormats={{
                                                days: 'M/YYYY',
                                                months: 'M/YYYY',
                                                year: 'YYYY',
                                            }}
                                            customStyle={{
                                                color: 'black',
                                                fontFamily: 'Trivia Sans Regular Bold, sans-serif',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        {this.props.translates.dueDaysFor3monthsText}
                                        <CustomDate
                                            date={this.props?.data?.subject?.quarter?.endDate?.value}
                                            precision={this.props?.data?.subject?.quarter?.endDate?.precision}
                                            precisionFormats={{
                                                days: 'M/YYYY',
                                                months: 'M/YYYY',
                                                year: 'YYYY',
                                            }}
                                            customStyle={{
                                                color: 'black',
                                                fontFamily: 'Trivia Sans Regular Bold, sans-serif',
                                            }}
                                        />
                                    </div>
                                ),
                            style: { minWidth: '165px', lineHeight: '1em' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: { width: '100px', borderRight: '1px solid #999' },
                        value: this.getInterval(legendItem),
                        header: {
                            label: this.props.translates.interval,
                            style: { lineHeight: '40px' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: { flexGrow: 1, fontWeight: isActiveSubject ? 900 : 'auto' },
                        value: legendItem.description,
                        header: {
                            label: this.props.translates.description,
                            style: { lineHeight: '40px' },
                        },
                    },
                ],
            }
        })

    render() {
        const {
            module,
            block,
            id,
            customData,
            originalData,
            translates: { riskIndicatorMessage, twelveMonthsTitle, threeMonthsTitle },
            translates,
            breakdown,
            graphData,
            selector,
            level,
        } = this.props
        const { YEAR_TYPE, QUARTER_TYPE } = types

        return (
            <div className={'payment-informations'}>
                <div className={'gdpr-row'}>
                    <GdprIcon id={id} block={block}>
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.gdprTooltip`}
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>,
                                br: <br />,
                                acz: (chunks) => (
                                    <a
                                        href="mailto:podpora.cz@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                                ask: (chunks) => (
                                    <a
                                        href="mailto:podpora.sk@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </GdprIcon>
                </div>
                <div className={'informations-header-wrap'}>
                    <div className={'year-header'}>
                        <div className={'header-title'}>{twelveMonthsTitle}</div>
                        {this.getRiskIndicator() && (
                            <div className={'risk-indicator-wrap'}>
                                <Counter
                                    leftValue={{
                                        value: `${this.getRiskIndicator()} %`,
                                        style: { color: '#DC0000', fontFamily: 'Trivia Sans Light, sans-serif' },
                                    }}
                                    description={riskIndicatorMessage}
                                />
                            </div>
                        )}
                    </div>
                    <div className={'quarter-header'}>
                        <div className={'header-title'}>{threeMonthsTitle}</div>
                    </div>
                </div>
                <div className={'informations-table-wrap'}>
                    <div className={'single-table-wrap'}>
                        <LegendTable legend={this.getSetup(YEAR_TYPE)} />
                    </div>
                    <div className={'single-table-wrap'}>
                        <LegendTable legend={this.getSetup(QUARTER_TYPE)} />
                    </div>
                </div>
                <div className={'legend-table-info-row'}>
                    <FormattedMessage
                        id={`${LOCALE_PREFIX}.greyValue`}
                        values={{
                            b: (chunks) => <b style={{ color: '#b3b3b3' }}>{chunks}</b>,
                        }}
                    />
                </div>
                <div className={'informations-table-wrap'}>
                    <DataTable
                        type={YEAR_TYPE}
                        subject={this.props?.data?.subject?.[YEAR_TYPE]}
                        sector={this.props?.data?.sector?.[YEAR_TYPE]}
                        translates={this.props.translates}
                        level={level}
                    />
                    <DataTable
                        type={QUARTER_TYPE}
                        subject={this.props?.data?.subject?.[QUARTER_TYPE]}
                        sector={this.props?.data?.sector?.[QUARTER_TYPE]}
                        translates={this.props.translates}
                        level={level}
                    />
                </div>
                {this.props.data?.sector?.name && (
                    <div className={'informations-table-wrap'}>
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.sectorName`}
                            values={{
                                sectorName: this.props.data.sector.name,
                                b: (chunks) => <b>{chunks}</b>,
                            }}
                        />
                    </div>
                )}
                <PaymentInformationsTerminology
                    module={module}
                    block={block}
                    id={id}
                    customData={customData}
                    translates={translates}
                />
                <PaymentBreakdown id={id} data={breakdown} />
                <PaymentInformationChart
                    id={id}
                    graphData={graphData?.[selector]}
                    tableData={originalData?.[1]?.data || []}
                    module={module}
                    block={block}
                    selector={selector}
                    customData={customData}
                />
            </div>
        )
    }
}
